import React, { useState } from "react";
import stableStringify from "json-stable-stringify";
import { gql, useQuery } from "urql";
import * as Tabs from "@radix-ui/react-tabs";
import { useParams } from "wouter";
import { FileSpreadsheet } from "lucide-react";

import { useQueryState } from "../../../components/src/use-query-state";
import { Grid, Column } from "../../../components/src";
import { isArrayOfObjects, getKeysFromArrayOfObjects } from "../../../components/src/common/Utils";
import { isValidMongoDBObjectID } from "../../../common/src/utils";
import { exportToXlsx } from "../../../components/src/common/exportToXlsx";
import { recursivelyRemoveKey } from "../../../common/src/utils";
import { Page } from "../../../components/src/Page";
import { ResponsiveTabsList } from "../../../components/src/ResponsiveTabsList";

interface IReportTabGrid {
    data: any[];
    transpose: boolean;
}

const ReportTabGrid = ({ data, transpose }: IReportTabGrid): React.ReactElement => {
    const isTable: boolean = isArrayOfObjects(data);
    let dataTable = [];

    if (isTable) {
        for (let i = 0; i !== data.length; ++i) {
            const row = data[i];
            if (row) {
                Object.keys(row).forEach((key) => {
                    if (data[i][key] !== null && typeof data[i][key] !== "string" && typeof data[i][key] !== "number") {
                        data[i][key] = stableStringify(data[i][key], { space: "  " });
                    }
                });
            }
        }
        dataTable = data;

        if (transpose) {
            dataTable = [];
            const rows = getKeysFromArrayOfObjects(data);

            Object.values(rows).forEach((key) => {
                const row = { "0": key };

                for (let i = 0; i !== data.length; ++i) {
                    const column = data[i];

                    if (column) {
                        row[(i + 1).toString()] = column[key];
                    }
                }
                dataTable.push(row);
            });
        }
    }

    const fields = getKeysFromArrayOfObjects(dataTable);

    return (
        <div>
            {isTable ? (
                <Grid data={dataTable} sortable tableClassName="table-xs" hideDownload={true}>
                    {fields.map((field, i) => (
                        <Column key={i} field={field} title={transpose ? "" : field} className={i === 0 ? "grid-column-sticky" : ""} />
                    ))}
                </Grid>
            ) : (
                <pre>{stableStringify(data, { space: "  " })}</pre>
            )}
        </div>
    );
};

const Table = ({ data, transpose, name }: { data: any; transpose: boolean; name: string }) => {
    if (!data) {
        data = { data: [] };
    }

    if (isArrayOfObjects(data)) {
        data = { data: data };
    }
    const tabs = {};
    Object.keys(data).forEach((key) => {
        tabs[key] = key;
    });

    const tabKeys = Object.keys(tabs);
    const defaultTab = tabKeys[0] || "";
    const [activeTab, setActiveTab] = useState(defaultTab);

    return (
        <div>
            <div className="flex flex-wrap -mx-2">
                <div className="w-full px-2 mb-4">
                    <button
                        id="export-excel"
                        className="export-link"
                        type="button"
                        onClick={() => {
                            //console.log(items);

                            exportToXlsx(Object.values(data), name + ".xlsx", Object.keys(data));
                        }}
                    >
                        <FileSpreadsheet className="Excel" />
                    </button>
                </div>
            </div>
            <div className="bg-gray-100 p-4 rounded">
                <Tabs.Root value={activeTab} onValueChange={setActiveTab} className="w-full">
                    <ResponsiveTabsList value={activeTab} onValueChange={setActiveTab}>
                        {tabKeys.map((tabKey) => (
                            <Tabs.Trigger
                                key={tabKey}
                                value={tabKey}
                                className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 border-b-2 border-transparent hover:border-gray-300 focus:outline-none focus:border-sky-500 data-[state=active]:border-sky-500 data-[state=active]:text-sky-600"
                            >
                                {tabs[tabKey]}
                            </Tabs.Trigger>
                        ))}
                    </ResponsiveTabsList>
                    {tabKeys.map((tabKey) => (
                        <Tabs.Content key={tabKey} value={tabKey} className="mt-4">
                            <ReportTabGrid data={data[tabKey]} transpose={transpose} />
                        </Tabs.Content>
                    ))}
                </Tabs.Root>
            </div>
        </div>
    );
};

const GET_REPORT = gql`
    query getReport($_id: GraphQLObjectId!) {
        report(_id: $_id) {
            _id
            status
            clientId
            clientName
            createTimestamp
            date
            name
            reportId
            sourceId
            sourceName
            type
            data
        }
    }
`;

export const ReportPage = (): React.ReactElement => {
    const { id } = useParams<{ id: string }>();
    const [{ fetching, error, data }] = useQuery({ query: GET_REPORT, variables: { _id: id }, pause: !isValidMongoDBObjectID(id) });
    const [table, setTable] = useQueryState("table", "false");
    const [transpose, setTranspose] = useQueryState("transpose", "false");

    if (!isValidMongoDBObjectID(id) && id !== "new") {
        return <Page header={"id: ''" + id + "'' is not a valid id format"} />;
    }

    if (fetching) return <div>Loading...</div>;
    if (error) return <div>`Error! ${error.message}`</div>;

    if (!data.report) return <div>no report found</div>;

    const report = recursivelyRemoveKey(data.report, "__typename");

    const reportData = report.data;
    delete report.data;

    return (
        <div>
            <button
                id="startlocalbutton"
                className="px-4 py-2 bg-sky-600 text-white rounded hover:bg-sky-700 transition-colors"
                type="button"
                onClick={() => {
                    if (table === "true") {
                        setTable("false");
                    } else {
                        setTable("true");
                    }
                }}
            >
                {table ? "Show as json" : "Show as table"}
            </button>
            {table === "true" ? (
                <button
                    id="btnTranspose"
                    className="ml-1 px-4 py-2 bg-sky-600 text-white rounded hover:bg-sky-700 transition-colors mr-2"
                    type="button"
                    onClick={() => {
                        if (transpose === "true") {
                            setTranspose("false");
                        } else {
                            setTranspose("true");
                        }
                    }}
                >
                    Transpose
                </button>
            ) : null}
            <pre className="mt-3">{stableStringify(report, { space: "  " })}</pre>
            {table === "true" ? (
                <Table data={reportData} transpose={transpose === "true"} name={report.name}></Table>
            ) : (
                <pre className="mt-3">{stableStringify(reportData, { space: "  " })}</pre>
            )}
        </div>
    );
};
